<template>
  <div>
    <div v-if="$apollo.loading" class="content">
      <Loader label="Loading Packages..." />
    </div>

    <Error
      v-if="!$apollo.loading && errors.length"
      message="We couldn't load the pricing packages."
      :errors="errors"
      class="mb-4 max-w-md mx-auto"
      :error-action="onRetryClick"
      error-action-label="Retry?"
    />

    <div
      v-if="!$apollo.loading && !errors.length && formattedPackages.length"
      class="content"
    >
      <div class="subscription-switch mb-2 lg:mb-4">
        <Button
          class="subscription-toggle"
          :class="{ active: subscription === 'Monthly' }"
          @click="onSubscriptionToggle"
          >Monthly</Button
        >
        <Button
          class="subscription-toggle relative"
          :class="{ active: subscription === 'Annual' }"
          @click="onSubscriptionToggle"
          >Yearly
          <span class="yearly-discount-tag animate__animated animate__flash"
            >Discounted</span
          >
          <img
            src="@/assets/icons/handdrawn-arrow.svg"
            alt="handdrawn arrow"
            class="yearly-discount-arrow animate__animated animate__flash"
        /></Button>
      </div>
      <!-- .subscription-switch -->

      <p class="text-center text-xs md:hidden text-support-lighter -mb-1">
        Swipe to see more&hellip;
      </p>

      <div
        class="mx-auto mb-10"
        :class="{
          'max-w-lg': formattedPackages.length < 3,
        }"
      >
        <PromoSlider
          :key="formattedPackages.length"
          :slides-to-show="1"
          :slides-to-scroll="1"
          :arrows="true"
          :dots="false"
          :scroll-lock="true"
          :see-all-button="false"
          :responsive-props="responsiveProps"
        >
          <template #slides>
            <div
              v-for="(pack, index) in formattedPackages"
              :key="pack.id"
              class="p-4 md:p-4 lg:p-6"
            >
              <Package
                :pack="pack"
                :index="index"
                :subscription="subscription"
                :subscriber-count="subscriberCount"
                :show-recommended="pack.recommended"
                @set-annual-subscription="onSubscriptionToggle"
              />
            </div>
          </template>
        </PromoSlider>

        <div
          class="flex flex-wrap justify-center items-center text-xs mt-2 lg: mt-4"
        >
          <a :href="customPricingMailTo">Request Custom Pricing</a>

          <span v-if="paidPlan" class="mx-1 text-grey-light">|</span>

          <router-link
            v-if="paidPlan"
            :to="{ name: 'Downgrade', params: { downgradeUrl } }"
            >Cancel Plan</router-link
          >
        </div>
        <!-- .flex flex-wrap -->
      </div>
      <!-- .max-w-xl mx-auto -->
    </div>

    <div v-else class="content">
      <h3 class="text-center text-base md:text-xl lg:text-2xl mb-2 md:mb-4">
        Enterprise Pricing
      </h3>

      <p
        class="text-center text-xs md:text-sm text-support-lighter mb-8 md:mb-10 max-w-xs mx-auto md:max-w-sm"
      >
        Based on your subscriber count you qualify for our highest tier of
        service. Get in touch and we'll create a unique plan for your needs.
      </p>
    </div>
  </div>
</template>

<script>
import { Account, Packages } from "@/graphql/queries";
import PromoSlider from "@/components/UI/PromoSlider";
import Loader from "@/components/UI/Loader";
import Error from "@/components/UI/Error";
import Package from "@/components/Account/Package";
import { getAccountId } from "@/lib/auth";

export default {
  apollo: {
    $query: {
      fetchPolicy: "cache-first",
    },
    account: {
      query: Account,
      update: (data) => data.account,
    },
    packages: {
      query: () => Packages,
      update: (data) => data.packages,
      result({ data, loading }) {
        if (!loading && data.packages?.length) this.setCurrentSubscription();
      },
      error(error) {
        this.errors.push(error);
      },
    },
  },
  inject: ["screens"],
  name: "PackageSelector",
  components: { Error, Loader, Package, PromoSlider },
  props: {
    showFreeTier: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      customPricingMailTo: `mailto:support@promo.ai?subject=Promo.ai Custom Pricing&body=%0D%0A%0D%0A${getAccountId()}`,
      errors: [],
      subscription: "Monthly",
    };
  },
  computed: {
    currentPackage() {
      return this.packages
        .filter((pack) => {
          const currentPlan = pack.plans.filter((plan) => plan.current);
          return currentPlan.length;
        })
        .reduce((acc) => acc);
    },
    downgradeUrl() {
      // Filter the packages for the free tier and return it's upgradeUrl from the monthly plan...
      return this.packages
        .filter((pack) => {
          return pack.id === "free";
        })
        .map((pack) => {
          return pack.plans
            ?.filter((plan) => plan.subscription === "Monthly")
            .reduce((acc) => acc);
        })
        .map((plan) => {
          return plan.upgradeUrl;
        })
        .reduce((acc) => acc);
    },
    formattedPackages() {
      return this.packages
        .filter((pack) => {
          // Filter the packages for the free tier and return it if showFreeTier is true
          // Return true for the rest of the packages
          return pack.id === "free" ? this.showFreeTier : true;
        })
        .filter((pack) => {
          // Only show packages that a user can subscribe to
          return pack.features?.maxEmails >= this.subscriberCount;
        })
        .map((pack, index) => {
          // Set the package as recommended if it's not the cheapest and they're on a free plan...
          if (
            !this.paidPlan &&
            pack.id !== this.currentPackage.id &&
            index === 1
          )
            pack.recommended = true;

          return pack;
        });
    },
    paidPlan() {
      // If there is a package with the id of free and it isn't the current package return true so the user can downgrade...
      return this.currentPackage.id !== "free";
    },
    responsiveProps() {
      const slidesToShow =
        this.formattedPackages?.length >= 3
          ? 3
          : this.formattedPackages?.length;

      const mobileSlidesToShow = slidesToShow > 2 ? 2 : slidesToShow;

      return [
        {
          breakpoint: this.screens.md,
          settings: {
            slidesToShow: mobileSlidesToShow,
          },
        },

        {
          breakpoint: this.screens.lg,
          settings: {
            slidesToShow,
          },
        },
      ];
    },
    subscriberCount() {
      return this.account?.subscribers?.subscriberCount;
    },
  },
  mounted() {
    this.$gtm.trackEvent({
      event: "promo-view-package",
      accountId: getAccountId(),
    });
  },
  methods: {
    onRetryClick() {
      this.$apollo?.queries?.packages.refetch();
    },
    onSubscriptionToggle() {
      this.subscription = this.subscription === "Annual" ? "Monthly" : "Annual";
    },
    setCurrentSubscription() {
      // Get the current plan...
      const currentPlan = this.currentPackage.plans
        .filter((plan) => plan.current)
        .reduce((acc) => acc);

      // Update the subscription prop if the user is on a paid plan...
      // This keeps the subscription to Annual by default
      if (this.paidPlan) this.subscription = currentPlan.subscription;
    },
  },
};
</script>

<style scoped>
.content {
  @apply py-2;
}

>>> .glider {
  @apply mb-0;
}

>>> .glider-next {
  right: -8px;

  @screen md {
    right: 21px;
  }
}

>>> .glider-prev {
  left: -8px;

  @screen md {
    left: 21px;
  }
}

.subscription-switch {
  @apply flex items-center justify-center;
}

.subscription-toggle {
  @apply no-underline py-1 px-3 text-xs text-support-lighter font-sans font-medium;

  &.active {
    @apply border shadow-inner rounded-full border-grey-lighter font-lato;
  }
}

.yearly-discount-tag {
  @apply text-success absolute font-normal font-sans;
  font-size: 11px;
  top: -15px;
  right: -52px;
  transform: rotate(5deg);
}

.yearly-discount-arrow {
  @apply h-auto absolute;
  top: 0px;
  transform: rotate(23deg);
  right: -24px;
  width: 21px;
}
</style>
